import { getToken, setToken } from "@/utils/auth";

/** 与网站 A 约定的查询名：任一带其一即可 */
const TOKEN_PARAM_NAMES = ["token", "access_token"] as const;

function getSearchParamToken(searchParams: URLSearchParams): string | null {
  for (const name of TOKEN_PARAM_NAMES) {
    const v = searchParams.get(name);
    if (v?.trim()) return v.trim();
  }
  return null;
}

/** 从 hash 中解析 token（支持 `#token=...`、`#/path?token=...`） */
function getTokenFromHash(hash: string): string | null {
  if (!hash || hash === "#") return null;
  const h = hash.startsWith("#") ? hash.slice(1) : hash;
  const qi = h.indexOf("?");
  const paramStr =
    qi >= 0
      ? h.slice(qi + 1)
      : h.includes("=") && !h.startsWith("/")
        ? h
        : "";
  if (!paramStr) return null;
  return getSearchParamToken(new URLSearchParams(paramStr));
}

function readIncomingTokenFromUrl(): string | null {
  const url = new URL(window.location.href);
  const fromQuery = getSearchParamToken(url.searchParams);
  if (fromQuery) return fromQuery;
  return getTokenFromHash(url.hash);
}

function hasAuthParamsInSearch(url: URL): boolean {
  return TOKEN_PARAM_NAMES.some((name) => url.searchParams.has(name));
}

function hasAuthParamsInHash(url: URL): boolean {
  if (url.hash.length <= 1) return false;
  const h = url.hash.slice(1);
  const qi = h.indexOf("?");
  const paramStr =
    qi >= 0
      ? h.slice(qi + 1)
      : h.includes("=") && !h.startsWith("/")
        ? h
        : "";
  if (!paramStr) return false;
  const hp = new URLSearchParams(paramStr);
  return TOKEN_PARAM_NAMES.some((name) => hp.has(name));
}

/**
 * 从地址栏读取网站 A 传入的 token，写入本地；若本地已有 token 则不覆盖。
 * 随后从 URL 中移除 token 相关参数（query / hash），避免留在历史记录与分享链接里。
 * 应在应用首次渲染前调用一次（见 main.tsx）。
 */
export function applyExternalTokenFromUrl(): void {
  const url = new URL(window.location.href);
  const incoming = readIncomingTokenFromUrl();
  const existing = getToken()?.trim() ?? "";

  if (incoming && !existing) {
    setToken(incoming);
  }

  if (!hasAuthParamsInSearch(url) && !hasAuthParamsInHash(url)) return;

  for (const name of TOKEN_PARAM_NAMES) {
    url.searchParams.delete(name);
  }

  if (url.hash.length > 1) {
    const h = url.hash.slice(1);
    const qi = h.indexOf("?");
    if (qi >= 0) {
      const base = h.slice(0, qi);
      const hp = new URLSearchParams(h.slice(qi + 1));
      for (const name of TOKEN_PARAM_NAMES) {
        hp.delete(name);
      }
      const rest = hp.toString();
      url.hash = rest ? `#${base}?${rest}` : base ? `#${base}` : "";
    } else if (h.includes("=") && !h.startsWith("/")) {
      const hp = new URLSearchParams(h);
      for (const name of TOKEN_PARAM_NAMES) {
        hp.delete(name);
      }
      const rest = hp.toString();
      url.hash = rest ? `#${rest}` : "";
    }
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, document.title, next);
}
